import type { SpawnPoint } from '../protocol.js';

export type V3 = [number, number, number];

export const MATERIALS = [
  'asphalt',
  'concrete',
  'pavers',
  'grass',
  'dirt',
  'plaster',
  'tile',
  'coping',
  'ramp',
  'rampSide',
  'deck',
  'metal',
  'railRed',
  'railYellow',
  'granite',
  'brick',
  'containerRed',
  'containerBlue',
  'containerOrange',
  'wood',
  'curb',
  'paintedWall',
] as const;
export type Material = (typeof MATERIALS)[number];

/** Collision triangle flag bits. */
export const SURFACE = {
  /** Rideable at any angle (ramps, pools, transitions). */
  transition: 1,
  /** Vert launches off this surface always return (perimeter banks). */
  contain: 2,
} as const;

export type RailKind = 'rail' | 'ledge' | 'coping';

export interface Rail {
  id: number;
  kind: RailKind;
  /** Polyline along the grindable top of the rail. */
  points: V3[];
  /** Closed loops connect the last point back to the first. */
  loop: boolean;
}

export interface RenderChunk {
  positions: number[];
  normals: number[];
  uvs: number[];
  colors: number[];
  indices: number[];
}

export type MapStyle = 'pool' | 'ramp' | 'block' | 'rail' | 'dirt' | 'grass' | 'wall' | 'plaza';

export interface MapShape {
  style: MapStyle;
  points: [number, number][];
  closed: boolean;
}

export type DecorKind =
  | 'lamp'
  | 'floodlight'
  | 'palm'
  | 'tree'
  | 'sign'
  | 'graffiti'
  | 'bleachers'
  | 'cone'
  | 'barrel'
  | 'paint';

export interface Decor {
  kind: DecorKind;
  p: V3;
  yaw: number;
  scale: number;
  /** Width/height for signs and graffiti panels. */
  w?: number;
  h?: number;
  text?: string;
  seed?: number;
}

export interface Zone {
  name: string;
  x: number;
  z: number;
}

export interface CollisionData {
  /** 9 floats per triangle. */
  positions: Float32Array;
  /** 9 floats per triangle (smooth vertex normals). */
  normals: Float32Array;
  flags: Uint8Array;
}

export interface Course {
  halfExtent: number;
  render: Map<Material, RenderChunk>;
  collision: CollisionData;
  rails: Rail[];
  shapes: MapShape[];
  decor: Decor[];
  zones: Zone[];
  spawns: SpawnPoint[];
}

export interface SurfaceOptions {
  mat: Material;
  collide?: boolean;
  render?: boolean;
  flags?: number;
  /** Vertex brightness multipliers (ambient-occlusion style shading). */
  shade?: [number, number, number];
}

export interface VertexExtras {
  normals?: [V3, V3, V3];
  uvs?: [[number, number], [number, number], [number, number]];
}

export function v3(x: number, y: number, z: number): V3 {
  return [x, y, z];
}
export function add(a: V3, b: V3): V3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}
export function sub(a: V3, b: V3): V3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
export function scale(a: V3, s: number): V3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}
export function dot(a: V3, b: V3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
export function cross(a: V3, b: V3): V3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
export function length(a: V3): number {
  return Math.hypot(a[0], a[1], a[2]);
}
export function normalize(a: V3): V3 {
  const l = length(a);
  return l > 1e-12 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 1, 0];
}
export function lerp3(a: V3, b: V3, t: number): V3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** A placement frame: local coordinates rotated by `yaw` about +Y, then translated. */
export class Frame {
  private readonly c: number;
  private readonly s: number;

  public constructor(
    public readonly x: number,
    public readonly y: number,
    public readonly z: number,
    public readonly yaw = 0,
  ) {
    this.c = Math.cos(yaw);
    this.s = Math.sin(yaw);
  }

  public p(lx: number, ly: number, lz: number): V3 {
    return [this.x + lx * this.c + lz * this.s, this.y + ly, this.z - lx * this.s + lz * this.c];
  }

  public d(lx: number, ly: number, lz: number): V3 {
    return [lx * this.c + lz * this.s, ly, -lx * this.s + lz * this.c];
  }

  public child(lx: number, ly: number, lz: number, yaw = 0): Frame {
    const p = this.p(lx, ly, lz);
    return new Frame(p[0], p[1], p[2], this.yaw + yaw);
  }
}

function chunk(): RenderChunk {
  return { positions: [], normals: [], uvs: [], colors: [], indices: [] };
}

/** Planar UV projection in metres, chosen from the dominant normal axis. */
function autoUv(p: V3, n: V3): [number, number] {
  const ax = Math.abs(n[0]);
  const ay = Math.abs(n[1]);
  const az = Math.abs(n[2]);
  if (ay >= ax && ay >= az) return [p[0], p[2]];
  if (ax >= az) return [p[2], p[1]];
  return [p[0], p[1]];
}

export class CourseBuilder {
  public readonly render = new Map<Material, RenderChunk>();
  public readonly rails: Rail[] = [];
  public readonly shapes: MapShape[] = [];
  public readonly decor: Decor[] = [];
  public readonly zones: Zone[] = [];
  private readonly colPositions: number[] = [];
  private readonly colNormals: number[] = [];
  private readonly colFlags: number[] = [];

  public tri(a: V3, b: V3, c: V3, options: SurfaceOptions, extras: VertexExtras = {}): void {
    let face = cross(sub(b, a), sub(c, a));
    const area = length(face);
    if (area < 1e-9) return;
    face = scale(face, 1 / area);
    let na = extras.normals?.[0] ?? face;
    let nb = extras.normals?.[1] ?? face;
    let nc = extras.normals?.[2] ?? face;
    const ua = extras.uvs?.[0];
    let ub = extras.uvs?.[1];
    let uc = extras.uvs?.[2];
    let shade = options.shade ?? [1, 1, 1];
    // Keep winding consistent with the supplied (smooth) normals.
    if (extras.normals && dot(face, add(add(na, nb), nc)) < 0) {
      [b, c] = [c, b];
      [nb, nc] = [nc, nb];
      [ub, uc] = [uc, ub];
      shade = [shade[0], shade[2], shade[1]];
      face = scale(face, -1);
    }
    if (!extras.normals) {
      na = face;
      nb = face;
      nc = face;
    }
    if (options.collide !== false) {
      this.colPositions.push(...a, ...b, ...c);
      this.colNormals.push(...na, ...nb, ...nc);
      this.colFlags.push(options.flags ?? 0);
    }
    if (options.render !== false) {
      let target = this.render.get(options.mat);
      if (!target) {
        target = chunk();
        this.render.set(options.mat, target);
      }
      const base = target.positions.length / 3;
      const verts: [V3, V3, [number, number] | undefined, number][] = [
        [a, na, ua, shade[0]],
        [b, nb, ub, shade[1]],
        [c, nc, uc, shade[2]],
      ];
      for (const [p, n, uv, s] of verts) {
        target.positions.push(p[0], p[1], p[2]);
        target.normals.push(n[0], n[1], n[2]);
        const coords = uv ?? autoUv(p, face);
        target.uvs.push(coords[0], coords[1]);
        target.colors.push(s, s, s);
      }
      target.indices.push(base, base + 1, base + 2);
    }
  }

  /** Adds a planar quad a→b→c→d; `facing` fixes winding so the face points that way. */
  public quad(a: V3, b: V3, c: V3, d: V3, facing: V3, options: SurfaceOptions): void {
    const n = normalize(facing);
    const extras: VertexExtras = { normals: [n, n, n] };
    this.tri(a, b, c, options, extras);
    this.tri(a, c, d, options, extras);
  }

  /**
   * A curved grid surface. `at(i, j)` returns the vertex position, smooth normal and uv in
   * metres for grid coordinates i∈[0,nu], j∈[0,nv]. `shadeAt` optionally darkens vertices.
   */
  public grid(
    nu: number,
    nv: number,
    at: (i: number, j: number) => { p: V3; n: V3; uv: [number, number] },
    options: SurfaceOptions,
    shadeAt?: (i: number, j: number) => number,
  ): void {
    const points: { p: V3; n: V3; uv: [number, number]; s: number }[][] = [];
    for (let i = 0; i <= nu; i += 1) {
      const row: { p: V3; n: V3; uv: [number, number]; s: number }[] = [];
      for (let j = 0; j <= nv; j += 1) row.push({ ...at(i, j), s: shadeAt?.(i, j) ?? 1 });
      points.push(row);
    }
    for (let i = 0; i < nu; i += 1) {
      for (let j = 0; j < nv; j += 1) {
        const p00 = points[i]![j]!;
        const p10 = points[i + 1]![j]!;
        const p11 = points[i + 1]![j + 1]!;
        const p01 = points[i]![j + 1]!;
        this.tri(
          p00.p,
          p10.p,
          p11.p,
          { ...options, shade: [p00.s, p10.s, p11.s] },
          { normals: [p00.n, p10.n, p11.n], uvs: [p00.uv, p10.uv, p11.uv] },
        );
        this.tri(
          p00.p,
          p11.p,
          p01.p,
          { ...options, shade: [p00.s, p11.s, p01.s] },
          { normals: [p00.n, p11.n, p01.n], uvs: [p00.uv, p11.uv, p01.uv] },
        );
      }
    }
  }

  /** Triangle fan over a polygon that is star-shaped from its first vertex. */
  public fan(points: V3[], facing: V3, options: SurfaceOptions): void {
    const n = normalize(facing);
    for (let i = 1; i < points.length - 1; i += 1) {
      this.tri(points[0]!, points[i]!, points[i + 1]!, options, { normals: [n, n, n] });
    }
  }

  public rail(kind: RailKind, points: V3[], loop = false): Rail {
    const rail: Rail = { id: this.rails.length, kind, points, loop };
    this.rails.push(rail);
    return rail;
  }

  public shape(style: MapStyle, points: [number, number][], closed = true): void {
    this.shapes.push({ style, points, closed });
  }

  public collision(): CollisionData {
    return {
      positions: new Float32Array(this.colPositions),
      normals: new Float32Array(this.colNormals),
      flags: new Uint8Array(this.colFlags),
    };
  }

  public get triangleCount(): number {
    return this.colFlags.length;
  }
}
